import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Infobulle } from './Infobulle';
import { useAuth } from '../hooks/useAuth';
import { useRessource } from '../hooks/useRessource';
import { api } from '../lib/apiClient';
import { ChangementMotDePasse } from './ChangementMotDePasse';
import { ModificationProfil } from './ModificationProfil';
import type { Entite } from '../types';
import { VERSION_COURANTE } from '../nouveautes';

/** Initiales affichees dans la pastille, comme reperage rapide. */
function initiales(prenom: string, nom: string): string {
  return `${prenom.charAt(0)}${nom.charAt(0)}`.toUpperCase();
}

export function MenuUtilisateur() {
  const { session, entiteActive, changerEntite, deconnecter } = useAuth();
  const [ouvert, setOuvert] = useState(false);
  const [profilOuvert, setProfilOuvert] = useState(false);
  const [motDePasseOuvert, setMotDePasseOuvert] = useState(false);
  const [bascule, setBascule] = useState<string | null>(null);
  const conteneur = useRef<HTMLDivElement>(null);
  const naviguer = useNavigate();

  // Couleurs d'identification des entites (RG-28) : la pastille du menu
  // reprend celle de l'entite active, pour que le repere soit le meme
  // partout.
  const { donnees: entites } = useRessource(() => api.get<Entite[]>('/api/entites'), []);

  // Un menu qui ne se ferme pas au clic exterieur reste ouvert par-dessus
  // le contenu et devient un obstacle.
  useEffect(() => {
    if (!ouvert) return;

    const auClic = (evenement: MouseEvent) => {
      if (!conteneur.current?.contains(evenement.target as Node)) setOuvert(false);
    };
    const auClavier = (evenement: KeyboardEvent) => {
      if (evenement.key === 'Escape') setOuvert(false);
    };

    document.addEventListener('mousedown', auClic);
    document.addEventListener('keydown', auClavier);
    return () => {
      document.removeEventListener('mousedown', auClic);
      document.removeEventListener('keydown', auClavier);
    };
  }, [ouvert]);

  if (!session) return null;

  const { utilisateur } = session;
  // Un POINT sur l'avatar tant que la version deployee n'a pas ete lue :
  // ouvrir l'ecran Nouveautes l'eteint, pour tous les postes de la personne.
  const nouveautesNonLues = utilisateur.nouveautesLues !== VERSION_COURANTE;
  const couleurActive =
    entites?.find((entite) => entite.id === entiteActive?.id)?.couleur_theme ?? '#5b5f6a';

  const basculer = async (entiteId: string) => {
    if (entiteId === session.entiteActiveId) {
      setOuvert(false);
      return;
    }
    setBascule(entiteId);
    try {
      await changerEntite(entiteId);
      setOuvert(false);
    } finally {
      setBascule(null);
    }
  };

  return (
    <div className="relative" ref={conteneur}>
      <button
        type="button"
        onClick={() => setOuvert((etat) => !etat)}
        aria-expanded={ouvert}
        aria-haspopup="menu"
        className={`flex items-center gap-2.5 rounded-full border py-1 pl-1 pr-3 transition ${
          ouvert
            ? 'border-encre-400 bg-white shadow-sm'
            : 'border-encre-200 bg-white hover:border-encre-300'
        }`}
      >
        <span className="relative">
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
            style={{ backgroundColor: couleurActive }}
            aria-hidden
          >
            {initiales(utilisateur.prenom, utilisateur.nom)}
          </span>
          {nouveautesNonLues && (
            <Infobulle texte="Des nouveautés sont à lire" className="absolute -right-0.5 -top-0.5 flex">
              <span className="h-2.5 w-2.5 rounded-full border-2 border-white bg-blue-600" />
            </Infobulle>
          )}
        </span>
        <span className="hidden text-sm font-medium text-encre-800 sm:block">
          {utilisateur.prenom} {utilisateur.nom}
        </span>
      </button>

      {ouvert && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-72 overflow-hidden rounded-lg border border-encre-200 bg-white shadow-lg"
        >
          <div className="border-b border-encre-100 px-4 py-3">
            <p className="text-sm font-medium text-encre-900">
              {utilisateur.prenom} {utilisateur.nom}
            </p>
            <p className="truncate text-xs text-encre-400">{utilisateur.email}</p>
            {/* Le role et l'entite active ne sont plus rappeles ici : la
                liste « Changer d'entite » juste dessous les montre, avec
                la pastille de couleur et la mention « actuel ». */}
          </div>

          {/* RG-28 : l'entite active est modifiable a tout moment. Le
              selecteur n'apparait que s'il y a un choix a faire. */}
          {session.entites.length > 1 && (
            <div className="border-b border-encre-100 py-1">
              <p className="px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-encre-400">
                Changer d’entité
              </p>
              {session.entites.map((entite) => {
                const active = entite.id === session.entiteActiveId;
                const couleur =
                  entites?.find((e) => e.id === entite.id)?.couleur_theme ?? '#5b5f6a';

                return (
                  <button
                    key={entite.id}
                    type="button"
                    role="menuitem"
                    disabled={bascule !== null}
                    onClick={() => void basculer(entite.id)}
                    className={`flex w-full items-center gap-2.5 px-4 py-2 text-left text-sm transition disabled:opacity-50 ${
                      active ? 'bg-encre-50 font-medium' : 'hover:bg-encre-50'
                    }`}
                  >
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: couleur }}
                      aria-hidden
                    />
                    <span className="flex-1">
                      {entite.code}
                      <span className="ml-1.5 text-xs text-encre-400">{entite.roleLibelle}</span>
                    </span>
                    {active && <span className="text-xs text-encre-400">actuel</span>}
                    {bascule === entite.id && <span className="text-xs text-encre-400">…</span>}
                  </button>
                );
              })}
            </div>
          )}

          <div className="py-1">
            <button
              type="button"
              role="menuitem"
              className="block w-full px-4 py-2 text-left text-sm hover:bg-encre-50"
              onClick={() => {
                setOuvert(false);
                setProfilOuvert(true);
              }}
            >
              Mes informations
            </button>
            <button
              type="button"
              role="menuitem"
              className="block w-full px-4 py-2 text-left text-sm hover:bg-encre-50"
              onClick={() => {
                setOuvert(false);
                setMotDePasseOuvert(true);
              }}
            >
              Changer mon mot de passe
            </button>
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center justify-between px-4 py-2 text-left text-sm hover:bg-encre-50"
              onClick={() => {
                setOuvert(false);
                naviguer('/nouveautes');
              }}
            >
              Nouveautés
              {nouveautesNonLues && (
                <span className="h-2 w-2 rounded-full bg-blue-600" aria-label="non lues" />
              )}
            </button>
          </div>

          <div className="border-t border-encre-100 py-1">
            <button
              type="button"
              role="menuitem"
              className="block w-full px-4 py-2 text-left text-sm text-red-700 hover:bg-red-50"
              onClick={() => void deconnecter()}
            >
              Se déconnecter
            </button>
          </div>
        </div>
      )}

      <ModificationProfil ouverte={profilOuvert} onFermer={() => setProfilOuvert(false)} />
      <ChangementMotDePasse
        ouverte={motDePasseOuvert}
        onFermer={() => setMotDePasseOuvert(false)}
      />
    </div>
  );
}
